import { z } from 'zod';
import { CalendarDate } from './project.js';
/**
 * Bấm giờ làm việc — APM-TIME-a, [plan-time-tracking.md](../../../docs/plan-time-tracking.md).
 *
 * ## Hai bảng, hai câu hỏi, không nối vào nhau bằng một tỉ lệ
 *
 * `work_schedule_shift` trả lời "người này **được** làm bao nhiêu giờ ngày mai"
 * — đầu vào của phép tính tải, của Gantt, của AI xếp lịch. `work_session` trả
 * lời "người này **đã** bấm bao nhiêu giờ tuần trước".
 *
 * Giao diện hiện số giờ theo lịch **làm bối cảnh đọc**, dạng chữ xám, không tô
 * màu và không hiện tỉ lệ "bấm/khai": một tỉ lệ như thế là một điểm tổng đội
 * lốt, đúng thứ `PerformanceRosterRow` bỏ công cấm.
 */
/** Lý do một đoạn bấm giờ kết thúc — khớp enum `WorkSessionEnd` của Prisma. */
export const WorkSessionEndReason = z.enum([
    'stopped',
    'paused',
    'switched',
    'shift_ended',
    'capped',
    /** Trần **đoạn ngoài ca** (`MAX_OVERTIME_SESSION_HOURS`) — APM-OT. */
    'overtime_capped',
    /** Việc đang bấm giờ vừa xong — APM-TIME-done. Không mời "Làm tiếp". */
    'task_done',
    /** Trần **tổng giờ một ngày** (`MAX_DAY_HOURS`) — APM-ACHEAT-d. Không mời "Làm tiếp". */
    'day_capped',
]);
/**
 * Khoá một tuần theo **ISO week**: `2026-W39`.
 *
 * Không dùng ngày đầu tuần. Một chuỗi ngày trên URL mời người ta gửi
 * `?week=2026-09-24` giữa tuần rồi tự hỏi tuần nào được chọn; ISO week không
 * có chỗ cho câu hỏi đó, và nó cũng là thứ `<input type="week">` nhận.
 */
export const WeekKey = z
    .string()
    .regex(/^\d{4}-W(0[1-9]|[1-4]\d|5[0-3])$/, 'Tuần phải có dạng YYYY-Www');
/** Trần một đoạn bấm giờ. Chạm trần thì job đóng với `capped` — kế hoạch §7. */
export const MAX_SESSION_HOURS = 12;
/**
 * Trần một đoạn bấm giờ **ngoài ca** — APM-OT, người dùng chốt 2026-09-22.
 *
 * ## Vì sao ngoài ca phải có trần riêng, và vì sao nó ngắn
 *
 * Đoạn trong ca được job đóng ở **mốc hết ca**, nên quên bấm dừng thì thiệt hại
 * dừng ở đó. Đoạn ngoài ca không có mốc nào như thế — thứ tự nhiên duy nhất là
 * trần 12 giờ, và khi ấy một người bấm start lúc 18h05 rồi quên bấm dừng sinh
 * ra **12 giờ làm thêm**: vừa sai vừa đáng báo động, ở đúng chỗ người ta sẽ tin
 * nó.
 *
 * Hai giờ là con số người dùng chốt, và nó đi kèm một vế bắt buộc: ai còn đang
 * làm thì **bấm Làm tiếp**. Đó là cùng một lập luận đã dựng nên luật "job không
 * bao giờ tự start lại" (`plan-time-tracking.md` §2) — hệ thống không đoán hộ
 * rằng một con người vẫn đang ngồi đó.
 *
 * ## ⚠️ Thứ Bảy, Chủ nhật và ngày lễ đi qua đúng đường này
 *
 * Ngày ấy không có ca nào, nên **mọi** đoạn của nó là đoạn ngoài ca — và câu
 * "ngày nghỉ mà làm thì vẫn phải có dừng tự động như ngày thường" được trả lời
 * bằng chính trần này, không cần một luật thứ hai.
 */
export const MAX_OVERTIME_SESSION_HOURS = 2;
/**
 * Trần **tổng giờ bấm trong một ngày** (ngày địa phương của tổ chức) —
 * APM-ACHEAT-d, người dùng chốt 2026-10-06.
 *
 * Hai trần trên là trần của **một đoạn**. Không có trần ngày thì cứ mỗi hai giờ
 * ngoài ca lại bấm "Làm tiếp" là một đoạn mới, và một ngày có thể ghi 20 giờ
 * mà không đoạn nào vi phạm gì. 14 giờ = một ca đủ (8) + làm thêm dài (6) —
 * trên mức ấy con số không còn mô tả một ngày làm việc của một người.
 *
 * Chạm trần thì job đóng ở **đúng mốc chạm** với `day_capped` và `start` từ
 * chối (409 `TIMESHEET_DAY_CAPPED`) tới hết ngày. Không mời "Làm tiếp".
 */
export const MAX_DAY_HOURS = 14;
/** Đối tượng đang bấm giờ: một task hoặc một issue, không bao giờ cả hai. */
export const TimesheetTarget = z.object({
    kind: z.enum(['task', 'issue']),
    id: z.uuid(),
    /** Mã hiển thị (`APM-12`). Issue dùng mã của chính nó. */
    key: z.string(),
    title: z.string(),
    projectId: z.uuid(),
    projectName: z.string(),
    dueDate: CalendarDate.nullable(),
    /**
     * Mốc hết hạn **chính xác tới giây** — thứ đồng hồ đếm ngược đếm tới.
     *
     * Khác `dueDate` ở chỗ nó là một thời điểm, không phải một ngày: task hết
     * hạn vào **cuối** ngày `dueDate`, còn vấn đề vận hành có hạn cam kết SLA
     * mang sẵn giờ phút. Đếm ngược tới nửa đêm **đầu** ngày `dueDate` sẽ báo quá
     * hạn suốt cả ngày cuối cùng — tức là sai ở đúng ngày người ta cần nó đúng.
     */
    dueAt: z.iso.datetime().nullable(),
    /**
     * Mốc bắt đầu của cửa sổ làm việc — mẫu số của "còn bao nhiêu phần trăm thời
     * gian".
     *
     * `startDate` của task nếu có. Không có thì `null`, và giao diện lấy mốc bắt
     * đầu bấm giờ làm mẫu số — xem `countdownOf`. Hai mẫu số khác nhau nên con số
     * phần trăm phải được đọc kèm cái nó dựa vào, và đó là lý do trường này lộ ra
     * thay vì để server tự chốt một tỉ lệ.
     */
    windowStartAt: z.iso.datetime().nullable(),
    /** Chỉ task mới có tiến độ; issue luôn `null`. */
    progressPct: z.number().int().min(0).max(100).nullable(),
});
export const ActiveWorkSession = z.object({
    id: z.uuid(),
    startedAt: z.iso.datetime(),
    target: TimesheetTarget,
    /**
     * Số phút đã chạy tính tới `serverNow`.
     *
     * Client cộng tiếp bằng đồng hồ của chính nó thay vì hỏi lại mỗi giây — xem
     * docblock của `TimesheetState`.
     */
    minutes: z.number().int().nonnegative(),
    /** Số ngày đã quá hạn của việc đang chạy. `null` = không có hạn, hoặc chưa quá. */
    overdueDays: z.number().int().positive().nullable(),
});
export const PausedWorkSession = z.object({
    id: z.uuid(),
    endedAt: z.iso.datetime(),
    endReason: WorkSessionEndReason,
    target: TimesheetTarget,
});
/** Một ca làm việc, đã quy ra mốc tuyệt đối của ngày hôm nay. */
export const TimesheetShift = z.object({ start: z.iso.datetime(), end: z.iso.datetime() });
/**
 * Trạng thái bấm giờ của người đang đăng nhập — cái nút ở góc màn hình đọc nó.
 *
 * ## ⚠️ Trả **dữ liệu thô**, không trả một chữ `"working" | "off"`
 *
 * Tính sẵn ở server thì client phải hỏi lại mỗi phút để biết nghỉ trưa đã tới
 * chưa — một request mỗi phút cho mỗi tab đang mở. Trả ca làm việc và
 * `serverNow` thì client tự lật trạng thái bằng đồng hồ của nó, và chỉ hỏi lại
 * mỗi phút để đồng bộ.
 */
export const TimesheetState = z.object({
    serverNow: z.iso.datetime(),
    /**
     * Múi giờ của tổ chức — APM-TZ-b. Giao diện đọc giờ của ca theo nó, không
     * theo UTC và không theo máy người xem: người mở app trên máy đặt giờ khác
     * vẫn phải thấy "ca tiếp theo lúc 13:00" đúng như lịch họ đã khai.
     */
    timezone: z.string(),
    active: ActiveWorkSession.nullable(),
    /** Việc đang tạm dừng — `null` khi người này đang rảnh. */
    paused: PausedWorkSession.nullable(),
    /** Ca làm việc **hôm nay**, đã trừ nghỉ lễ và nghỉ phép. Rỗng = hôm nay nghỉ. */
    shifts: z.array(TimesheetShift),
    /** Ca kế tiếp sau `serverNow`, để nói "còn bao lâu nữa tới giờ làm". */
    nextShiftStart: z.iso.datetime().nullable(),
    /**
     * Đoạn bị job đóng ở mốc hết ca, và giờ đã sang ca mới ⇒ mời "tiếp tục?".
     *
     * Job **không bao giờ** tự start lại: một người nghỉ chiều mà quên stop sẽ
     * được ghi giờ họ không làm, và timesheet mất tin cậy ở đúng chỗ nó cần nhất.
     */
    resumeOffer: z.boolean(),
    /** Tổng phút đã bấm hôm nay, kể cả đoạn đang chạy. */
    todayMinutes: z.number().int().nonnegative(),
});
/**
 * Bắt đầu bấm giờ. **Đúng một** trong hai id có giá trị.
 *
 * Không có input cho "switch": start **là** switch — handler đóng đoạn đang mở
 * của chính người đó với `switched` trong cùng transaction.
 */
export const StartWorkSessionInput = z
    .object({
    taskId: z.uuid().optional(),
    issueId: z.uuid().optional(),
    note: z.string().trim().max(500).optional(),
})
    .superRefine((value, ctx) => {
    const count = Number(value.taskId !== undefined) + Number(value.issueId !== undefined);
    if (count !== 1) {
        ctx.addIssue({
            code: 'custom',
            path: ['taskId'],
            message: 'Phải chọn đúng một việc: task hoặc vấn đề vận hành',
        });
    }
});
/**
 * Một việc đã bấm giờ trong **một ngày** — APM-TIME-e, người dùng yêu cầu
 * 2026-09-22.
 *
 * Một việc kéo dài ba ngày là **ba** mục ở ba cột, mỗi mục mang số phút của
 * đúng ngày đó. Gộp thành một mục "12 giờ" đặt ở ngày bắt đầu là cách nhanh
 * nhất để một bảng chấm công nói sai về một ngày cụ thể — và ngày cụ thể chính
 * là thứ người ta mở bảng này ra để tra.
 */
export const TimesheetEntry = z.object({
    kind: z.enum(['task', 'issue']),
    id: z.uuid(),
    key: z.string(),
    title: z.string(),
    projectId: z.uuid(),
    projectName: z.string(),
    /**
     * Màu nhận dạng của dự án (`ProjectColor`), hoặc `null` khi dự án chưa chọn
     * màu. Giao diện tô nền thẻ việc theo nó, nên **cùng một dự án thì cùng một
     * màu** ở mọi cột — đó là thứ làm một tuần nhiều dự án đọc được bằng mắt.
     */
    projectColor: z.string().nullable(),
    minutes: z.number().int().nonnegative(),
    /**
     * Trong `minutes`, bao nhiêu phút nằm **ngoài ca** — APM-OT-a.
     *
     * ⚠️ **Tập con** của `minutes`, không phải một số cộng thêm: `minutes: 120,
     * overtimeMinutes: 45` nghĩa là *làm 2 giờ, trong đó 45 phút ngoài ca*.
     *
     * Ở tầng này để trả lời câu hỏi kế tiếp sau "tuần này làm thêm mấy giờ": **để
     * làm gì**. Một tổng giờ làm thêm không nói được việc nào đã kéo người ta ở
     * lại, và đó đúng là thứ cần biết để nó không lặp lại tuần sau.
     */
    overtimeMinutes: z.number().int().nonnegative(),
});
/** Một ngày trong bảng tuần. */
export const TimesheetDay = z.object({
    date: CalendarDate,
    /**
     * `null` = **chưa bấm giờ ngày đó**, không phải "làm 0 giờ".
     *
     * `0` là một khẳng định, `null` là sự vắng mặt của dữ liệu — và với một
     * timesheet tự nguyện thì phần lớn ô trống là loại thứ hai.
     */
    minutes: z.number().int().nonnegative().nullable(),
    /** Trần theo lịch làm việc, đã trừ lễ và nghỉ phép. `0` = ngày nghỉ. */
    scheduledMinutes: z.number().int().nonnegative(),
    /**
     * Phần của `minutes` nằm **ngoài ca** — APM-OT-a.
     *
     * `0` khi làm trọn trong ca. Bằng `minutes` ở ngày `scheduledMinutes = 0`
     * (thứ Bảy, ngày lễ, ngày người ấy nghỉ phép): ngày không có ca nào thì mọi
     * phút đều ngoài ca, và đó là **chủ đích** — ai làm bù cuối tuần phải thấy nó.
     *
     * ⚠️ `0` khi `minutes` là `null`. Không phải `null`: đây là một phép trừ trên
     * một tập rỗng, không phải một phép đo chưa có.
     */
    overtimeMinutes: z.number().int().nonnegative(),
    /**
     * Những việc đã bấm giờ trong ngày, nhiều phút nhất trước.
     *
     * Rỗng khi chưa bấm gì — và khi ấy `minutes` cũng là `null`, hai thứ không
     * bao giờ được lệch nhau.
     */
    entries: z.array(TimesheetEntry),
});
export const TimesheetProjectSlice = z.object({
    projectId: z.uuid(),
    projectName: z.string(),
    minutes: z.number().int().nonnegative(),
});
export const TimesheetRow = z.object({
    userId: z.uuid(),
    userName: z.string(),
    /** Bảy ngày, **thứ Hai trước** — thứ tự ISO, mỗi ngày mang `date` của chính nó. */
    days: z.array(TimesheetDay).length(7),
    totalMinutes: z.number().int().nonnegative(),
    scheduledMinutes: z.number().int().nonnegative(),
    /** Tổng phút ngoài ca của cả tuần — **tập con** của `totalMinutes`. */
    overtimeMinutes: z.number().int().nonnegative(),
    /**
     * Trong `overtimeMinutes`, phần đến từ đoạn hệ thống **phải tự đóng** vì chạm
     * trần `MAX_SESSION_HOURS` — APM-OT-b, plan-overtime.md §3.2.
     *
     * ## Vì sao con số này phải đứng riêng
     *
     * Đoạn bắt đầu ngoài ca không bị job auto-pause đóng theo ca (đó là chủ đích
     * — nếu không thì làm thêm giờ không ghi lại được). Nó chỉ bị đóng ở trần 12
     * giờ. Nên một người bấm start lúc 18h05 rồi **quên bấm dừng** sinh ra 12 giờ
     * "làm thêm" — vừa sai vừa đáng báo động, ở đúng chỗ người ta sẽ tin nó.
     *
     * Vẫn cộng vào `overtimeMinutes` (bỏ đi thì mất luôn những ca làm thêm dài
     * thật), nhưng nói ra để người đọc biết con số nào đáng tin **trước khi** hỏi
     * chuyện một con người về nó.
     */
    cappedOvertimeMinutes: z.number().int().nonnegative(),
    /** Số ngày **có ca** trong tuần này — mẫu số của `avgMinutesPerScheduledDay`. */
    scheduledDays: z.number().int().min(0).max(7),
    /**
     * Trung bình mỗi ngày làm việc: `totalMinutes / scheduledDays`.
     *
     * Chia cho **số ngày có ca**, không chia 7 (ai cũng trông như làm 4 giờ/ngày)
     * và không chia số ngày có bấm giờ (người bấm đúng một ngày sẽ đứng đầu bảng).
     * `null` khi tuần đó không có ngày làm việc nào, **hoặc** khi chưa bấm giờ
     * lần nào — một tuần trống mà hiện "0,0 giờ/ngày" là đọc một khoảng trống
     * thành một phép đo.
     */
    avgMinutesPerScheduledDay: z.number().nullable(),
    byProject: z.array(TimesheetProjectSlice),
    /** Hàng của chính người đang xem — giao diện ghim nó lên đầu. */
    isMe: z.boolean(),
});
export const TimesheetWeek = z.object({
    week: WeekKey,
    from: CalendarDate,
    to: CalendarDate,
    rows: z.array(TimesheetRow),
});
const DAY_MS = 24 * 60 * 60 * 1000;
/**
 * Khoá ISO week của một mốc, theo **UTC**.
 *
 * Cùng quy ước với cả repo: `Weekday` khai rõ "đúng bằng `Date.getUTCDay()`",
 * `calendarDayOf` cắt chuỗi ISO, và `prisma.service.ts` đặt `timezone: 'Z'`.
 */
export function isoWeekKey(at) {
    const thursday = isoThursdayOf(at);
    const year = thursday.getUTCFullYear();
    const firstThursday = isoThursdayOf(new Date(Date.UTC(year, 0, 4)));
    const week = 1 + Math.round((thursday.getTime() - firstThursday.getTime()) / (7 * DAY_MS));
    return `${year}-W${String(week).padStart(2, '0')}`;
}
/** Thứ Hai 00:00:00.000 UTC của tuần ấy. */
export function isoWeekStart(key) {
    const [yearPart, weekPart] = key.split('-W');
    const year = Number(yearPart);
    const week = Number(weekPart);
    const firstThursday = isoThursdayOf(new Date(Date.UTC(year, 0, 4)));
    return new Date(firstThursday.getTime() + (week - 1) * 7 * DAY_MS - 3 * DAY_MS);
}
/**
 * Nửa khoảng `[start, end)` của một tuần — thứ Hai tới thứ Hai kế tiếp.
 *
 * Nửa khoảng chứ không phải khoảng đóng: `ended_at < end` không bỏ sót một
 * phần nghìn giây nào, còn `<= chủ nhật 23:59:59.999` thì bỏ sót đúng những
 * mốc rơi vào phần nghìn giây cuối cùng — thứ không ai thử tay bao giờ.
 */
export function isoWeekRange(key) {
    const start = isoWeekStart(key);
    return { start, end: new Date(start.getTime() + 7 * DAY_MS) };
}
/** Tuần liền trước/sau: `shiftIsoWeek('2026-W01', -1)` → `'2025-W52'`. */
export function shiftIsoWeek(key, delta) {
    return isoWeekKey(new Date(isoWeekStart(key).getTime() + delta * 7 * DAY_MS));
}
/**
 * Số phút của một đoạn. Đoạn đang mở tính tới `now`.
 *
 * Không bao giờ âm: đồng hồ client lệch sau đồng hồ server là chuyện thường, và
 * một đoạn dài `-3` phút sẽ trừ vào tổng của cả ngày.
 */
export function sessionMinutes(session, now) {
    const started = new Date(session.startedAt).getTime();
    const ended = session.endedAt ? new Date(session.endedAt).getTime() : now.getTime();
    return Math.max(0, Math.floor((ended - started) / 60_000));
}
/** Thứ Năm của tuần chứa `at` — mốc neo của ISO 8601 (tuần thuộc năm chứa thứ Năm của nó). */
function isoThursdayOf(at) {
    const midnight = Date.UTC(at.getUTCFullYear(), at.getUTCMonth(), at.getUTCDate());
    // `getUTCDay()`: 0 = Chủ nhật. ISO coi Chủ nhật là ngày **thứ bảy** của tuần.
    const isoWeekday = at.getUTCDay() === 0 ? 7 : at.getUTCDay();
    return new Date(midnight + (4 - isoWeekday) * DAY_MS);
}
/**
 * Cửa sổ nhìn lại của tổng giờ làm thêm ở hồ sơ một người — APM-OT-d.
 *
 * 90 ngày, cùng con số với `PERFORMANCE_ROSTER_WINDOW_DAYS`: câu người quản lý
 * thật sự hỏi khi ngồi xuống với một người là *"quý vừa rồi thế nào"*. Một tuần
 * thì đã có bảng timesheet trả lời; một năm thì trộn cả những đợt đã qua từ lâu
 * và không còn nói gì về hiện tại.
 */
export const OVERTIME_SUMMARY_DAYS = 90;
/**
 * Tổng giờ làm thêm của **một người** trong một cửa sổ nhìn lại — APM-OT-d,
 * [docs/plan-overtime.md](../../../docs/plan-overtime.md) §4.
 *
 * ## ⚠️ Đây là một con số **tải trọng**, không phải một chiều hiệu suất
 *
 * Nó đứng cạnh `PersonalLoad` (người này đang gánh bao nhiêu), **không** cạnh
 * `PerformanceProfile` (người này làm việc thế nào). Giờ làm thêm cao gần như
 * luôn là dấu hiệu của ước lượng sai hoặc quá tải — một vấn đề cần sửa, không
 * phải một thành tích cần khen.
 *
 * Và nó cố ý **không** có mặt ở `PerformanceRosterRow`: một cột giờ làm thêm
 * trong một bảng vốn để so sánh sẽ được đọc như một cột công trạng, và khi ấy
 * ứng dụng âm thầm thưởng cho việc ở lại muộn. Cùng lý lẽ đã cấm `score` /
 * `rank` / `band` ở bảng ấy (người dùng chốt 2026-09-22).
 */
export const OvertimeSummary = z.object({
    from: z.iso.datetime(),
    to: z.iso.datetime(),
    /** Tổng phút đã bấm giờ trong cửa sổ — mẫu số để đọc con số dưới. */
    workedMinutes: z.number().int().nonnegative(),
    /** **Tập con** của `workedMinutes`: phần nằm ngoài ca. */
    overtimeMinutes: z.number().int().nonnegative(),
    /** Trong `overtimeMinutes`, phần đến từ đoạn hệ thống phải tự đóng. */
    cappedOvertimeMinutes: z.number().int().nonnegative(),
    /** Số ngày **có bấm giờ ngoài ca** — "10 giờ rải 8 ngày" khác "10 giờ trong 1 đêm". */
    overtimeDays: z.number().int().nonnegative(),
});
/**
 * Trong một khung bấm giờ, việc đã tiến thêm được bao nhiêu — APM-TSOUT,
 * người dùng yêu cầu 2026-10-22.
 *
 * Suy ra **từ `event_log`** của task, tính trên khoảng `[bắt đầu, kết thúc +
 * WORK_SESSION_OUTCOME_GRACE_MINUTES]` của khung: người ta hay bấm dừng giờ rồi
 * mới kéo thẻ sang Xong, và một việc xong lúc 10:46 với khung dừng lúc 10:45 mà
 * hiện "không xong trong khung" là nói sai đúng câu người xem muốn hỏi.
 *
 * ⚠️ Đây là **trạng thái của việc**, không phải công của riêng một người: ai
 * đổi tiến độ trong khung (người khác, AI, đồng bộ) cũng được tính.
 */
export const WorkSessionOutcome = z.object({
    /** Tiến độ ở đầu và cuối khung, 0–100. */
    progressFrom: z.number().int().min(0).max(100),
    progressTo: z.number().int().min(0).max(100),
    /** `progressTo − progressFrom` — **có thể âm** khi tiến độ bị hạ. */
    progressDelta: z.number().int(),
    /**
     * Số mục checklist **ròng**: so trạng thái đầu và cuối khung theo id mục. Tick
     * rồi bỏ tick trong cùng khung thì không tính — người dùng chốt 2026-10-22.
     */
    checklistChecked: z.number().int().nonnegative(),
    /** Mục đã tick ở đầu khung mà cuối khung không còn tick. */
    checklistUnchecked: z.number().int().nonnegative(),
    /** `x` trong `x/y` ở cuối khung; `null` khi việc không có checklist. */
    checklistDone: z.number().int().nonnegative().nullable(),
    checklistTotal: z.number().int().nonnegative().nullable(),
    /** Có chuyển sang cột loại `done` trong khung (kể cả phần trễ cho phép) không. */
    completed: z.boolean(),
    completedAt: z.iso.datetime().nullable(),
});
/** Phần trễ sau khi khung kết thúc vẫn tính vào khung — người dùng chốt 15 phút, 2026-10-22. */
export const WORK_SESSION_OUTCOME_GRACE_MINUTES = 15;
/** Một phiên bấm giờ trên một việc, kèm kết quả của nó. */
export const TimesheetEntrySession = z.object({
    id: z.uuid(),
    startedAt: z.iso.datetime(),
    /** `null` = đang chạy; kết quả khi ấy tính tới hiện tại. */
    endedAt: z.iso.datetime().nullable(),
    endReason: WorkSessionEndReason.nullable(),
    /** Số phút **trong ngày đang xem** — khớp số trên thẻ của lưới tuần. */
    minutes: z.number().int().nonnegative(),
    outcome: WorkSessionOutcome,
});
/**
 * Chi tiết một thẻ việc của lưới tuần (một người · một task · một ngày) —
 * APM-TSOUT. `GET /timesheet/entry`.
 */
export const TimesheetEntryDetail = z.object({
    userId: z.uuid(),
    userName: z.string(),
    taskId: z.uuid(),
    key: z.string(),
    title: z.string(),
    date: CalendarDate,
    /** Sớm nhất trước. */
    sessions: z.array(TimesheetEntrySession),
    /**
     * Cộng dồn của các phiên trong ngày — **không** đo từ đầu phiên đầu tới cuối
     * phiên cuối: khoảng giữa hai phiên là lúc người ấy làm việc khác.
     * `progressFrom` là đầu phiên đầu, `progressTo`/checklist `x/y` là cuối phiên cuối.
     * `null` khi ngày ấy không có phiên nào.
     */
    total: WorkSessionOutcome.nullable(),
});
export const TimesheetEntryQuery = z.object({
    userId: z.uuid(),
    taskId: z.uuid(),
    date: CalendarDate,
});
//# sourceMappingURL=timesheet.js.map